import { Link } from 'react-router-dom';
import { MessageSquare, Clock, ChevronRight } from 'lucide-react';
import { SupportTicket } from '@outlaw-oasis/types';
import { formatRelativeTime } from '@outlaw-oasis/utils';
import { TicketStatusBadge } from './TicketStatusBadge';
import { TicketPriorityBadge } from './TicketPriorityBadge';

interface TicketCardProps {
  ticket: SupportTicket;
  isAdmin?: boolean;
}

export function TicketCard({ ticket, isAdmin = false }: TicketCardProps) {
  const linkTo = isAdmin ? `/admin/support/${ticket.id}` : `/support/${ticket.id}`;
  const isUnread = isAdmin ? !ticket.isReadByAdmin : !ticket.isReadByUser;

  return (
    <Link
      to={linkTo}
      className={`block bg-surface rounded-xl border transition-all hover:border-gold/40 ${
        isUnread ? 'border-gold/30 bg-gold/5' : 'border-gold/20'
      }`}
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-mono text-gold">{ticket.ticketNumber}</span>
              {isUnread && (
                <span className="w-2 h-2 rounded-full bg-gold animate-pulse" />
              )}
            </div>
            <h3 className="text-cream font-medium truncate mb-2">
              {ticket.subject}
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              <TicketStatusBadge status={ticket.status} size="sm" />
              <TicketPriorityBadge priority={ticket.priority} size="sm" />
              {ticket.category && (
                <span className="text-xs text-text-muted bg-leather px-2 py-0.5 rounded">
                  {ticket.category.name}
                </span>
              )}
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-text-muted flex-shrink-0 mt-1" />
        </div>

        <div className="flex items-center gap-4 mt-4 pt-3 border-t border-gold/10 text-xs text-text-muted">
          <div className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            <span>{formatRelativeTime(ticket.createdAt)}</span>
          </div>
          {ticket.messageCount !== undefined && (
            <div className="flex items-center gap-1">
              <MessageSquare className="w-3.5 h-3.5" />
              <span>{ticket.messageCount} messages</span>
            </div>
          )}
          {isAdmin && ticket.user && (
            <div className="flex items-center gap-1 ml-auto">
              <span>by {ticket.user.username}</span>
            </div>
          )}
          {ticket.lastReplyAt && (
            <div className="ml-auto">
              Last reply {formatRelativeTime(ticket.lastReplyAt)}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
